import React, { useEffect, useRef, useState, memo } from "react";
import { Box, Skeleton } from "@mui/material";
import { getFileContent } from "../../api/Proyectos/projectFilesApi";

const imagesCache = new Map();

const loadImagesFor = async (
  numPresupuesto,
  idMuestra,
  temporada,
  marca,
  designer,
  proveedor,
) => {
  const cacheKey = numPresupuesto
    ? `P_${numPresupuesto}`
    : idMuestra
      ? `M_${idMuestra}_${temporada}_${marca}_${designer}_${proveedor}`
      : null;

  if (!cacheKey) return [];

  if (imagesCache.has(cacheKey)) {
    return imagesCache.get(cacheKey);
  }

  try {
    let formatted = [];

    // PRODUCCIÓN
    if (numPresupuesto) {
      const files = await getFileContent(numPresupuesto);

      formatted = files.map((file) => ({
        url: `data:${file.mimeType};base64,${file.data}`,
        fileName: file.fileName,
      }));
    }

    // MUESTRAS
    if (idMuestra) {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/files/muestra/${idMuestra}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        },
      );

      if (!res.ok) {
        imagesCache.set(cacheKey, []);
        return [];
      }

      const files = await res.json();

      formatted = await Promise.all(
        files.map(async (f) => {
          const fileRes = await fetch(
            `${process.env.REACT_APP_API_URL}/api/files/readfile?path=${encodeURIComponent(f.path)}`,
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem("token")}`,
              },
            },
          );

          if (!fileRes.ok) return null;

          const fileData = await fileRes.json();

          return {
            url: `data:${fileData.mimeType};base64,${fileData.data}`,
            fileName: f.name,
          };
        }),
      );
      formatted = formatted.filter(Boolean);
    }

    imagesCache.set(cacheKey, formatted);
    return formatted;
  } catch {
    imagesCache.set(cacheKey, []);
    return [];
  }
};

const ProductPhotosCell = memo(function ProductPhotosCell({
  numPresupuesto,
  idMuestra,
  temporada,
  marca,
  designer,
  proveedor,
  onImageClick,
  deferUntilVisible = false,
}) {
  const rootRef = useRef(null);
  const [shouldLoad, setShouldLoad] = useState(!deferUntilVisible);
  const [images, setImages] = useState(null);

  useEffect(() => {
    if (!deferUntilVisible) {
      setShouldLoad(true);
      return undefined;
    }

    setShouldLoad(false);
    setImages(null);

    const el = rootRef.current;
    if (!el) return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShouldLoad(true);
          observer.disconnect();
        }
      },
      { rootMargin: "240px 0px" },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [
    deferUntilVisible,
    numPresupuesto,
    idMuestra,
    temporada,
    marca,
    designer,
    proveedor,
  ]);

  useEffect(() => {
    if (!shouldLoad) return undefined;

    let mounted = true;

    const load = async () => {
      setImages(null);

      const data = await loadImagesFor(
        numPresupuesto,
        idMuestra,
        temporada,
        marca,
        designer,
        proveedor,
      );

      if (mounted) {
        setImages(data);
      }
    };

    load();

    return () => {
      mounted = false;
    };
  }, [
    shouldLoad,
    numPresupuesto,
    idMuestra,
    temporada,
    marca,
    designer,
    proveedor,
  ]);

  if (!shouldLoad || images === null) {
    return (
      <Box ref={rootRef}>
        <Skeleton
          variant="rectangular"
          width={50}
          height={50}
          sx={{ borderRadius: 1 }}
        />
      </Box>
    );
  }

  if (images.length === 0) {
    return (
      <Box
        ref={rootRef}
        sx={{
          width: 50,
          height: 50,
          background: "#ddd",
          borderRadius: 1,
        }}
      />
    );
  }

  return (
    <Box ref={rootRef} sx={{ display: "flex", gap: 1 }}>
      {images.slice(0, 2).map((img) => (
        <Box
          key={img.url}
          component="img"
          src={img.url}
          alt={img.fileName}
          onClick={(e) => {
            e.stopPropagation();
            onImageClick?.(e.currentTarget, img.url);
          }}
          sx={{
            width: 50,
            height: 50,
            objectFit: "cover",
            borderRadius: 1,
            cursor: "pointer",
          }}
        />
      ))}
    </Box>
  );
});

export default ProductPhotosCell;
